import React from "react";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, Server, KeyRound, FileLock2, ShieldCheck } from "lucide-react";
import { HID } from "@/constants/cosecIds";

const CERTS = [
  "ISO 9001 Certified",
  "ISO 27001 Certified",
  "ISO 27701 Certified",
  "GDPR Compliant",
  "Secure AWS Cloud Hosting",
  "UK Companies House Authorised Software",
];

const PILLARS = [
  { Icon: Server, t: "Encrypted cloud" },
  { Icon: KeyRound, t: "Role-based access" },
  { Icon: FileLock2, t: "Immutable audit logs" },
  { Icon: ShieldCheck, t: "Independent audits" },
];

export default function Security() {
  return (
    <section className="py-20 md:py-28 bg-primary text-primary-foreground relative overflow-hidden">
      <div className="absolute inset-0 bg-dotgrid opacity-[0.07] pointer-events-none" />
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />

      <div className="container-cv relative grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            <span className="h-px w-8 bg-accent" /> Enterprise-grade security
          </span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.08]">
            Enterprise-Grade Security and{" "}
            <span className="text-accent">Data Protection.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
            Protecting sensitive company data is a top priority. CoSec uses encrypted
            cloud infrastructure, independent audits, and recognised security standards
            to help UK businesses manage records with confidence. We are committed to
            privacy, compliance, and secure access across every level of the platform.
          </p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PILLARS.map(({ Icon, t }, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.04] p-4 text-center">
                <Icon className="mx-auto h-5 w-5 text-accent" />
                <div className="mt-2 text-xs font-bold text-white/90">{t}</div>
              </div>
            ))}
          </div>

          <Button
            asChild
            data-testid={HID.ctaSecurity}
            className="mt-8 h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-semibold"
          >
            <a href="/contact">
              Learn About Security <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>

        <div className="lg:col-span-6">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8 backdrop-blur">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-accent" />
                <span className="text-xs font-bold uppercase tracking-wider text-white/80">Compliance posture</span>
              </div>
              <span className="text-xs font-mono text-white/40">audit · q4</span>
            </div>
            <ul className="mt-5 grid sm:grid-cols-2 gap-3">
              {CERTS.map((c, i) => (
                <li key={i} className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5">
                  <span className="grid h-6 w-6 place-items-center rounded-full bg-accent text-primary">
                    <ShieldCheck className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-xs md:text-sm font-medium text-white/90">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
